import { AscendancyEmbedView } from '@/components/embeds/Ascendancy'
import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingOverview } from '@/types/leveling'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useStore } from '@maxroll-media-group/common-libs'
import { EditableText } from 'components/editor/EditableText'
import { RichTextEditor } from 'components/editor/rich-text-editor/RichTextEditor'
import { banditOptions } from 'components/embeds/BuildList'
import { PantheonTooltip } from 'components/embeds/Pantheon'
import Paperdoll from 'components/embeds/Paperdoll'
import Data from 'data'
import { BuildsList } from 'leveling/widgets/builds-list'
import { ContentCreator } from 'leveling/widgets/content-creator'
import { CraftingGuides } from 'leveling/widgets/crafting-guides'
import { LevelingTitleBox } from 'leveling/widgets/leveling-title-box'
import { Strategies } from 'leveling/widgets/strategies'
import { StrengthsAndWeaknesses } from 'leveling/widgets/strengths-and-weaknesses'
import React from 'react'
import Store from 'store'
import { RawTooltip } from 'ui/tooltip'
import { WithStore, useEmbed } from '../common'
import { Widget } from '../widget'
import { DifficultyBar } from '../widgets/difficulty-bar'
import { PlannerLink } from '../widgets/planner-link'
import { PobLinks } from '../widgets/pob-links'
import './overview.scss'

function getYoutubeVideoId(link: string) {
  if (typeof link === 'string') {
    if (link.includes('watch?v=')) {
      return link.split('watch?v=')?.[1]?.split('&')?.[0]
    } else if (link.includes('embed/')) {
      return link.split('embed/')?.[1]?.split('?')?.[0]
    } else if (link.includes('youtu.be/')) {
      return link.split('youtu.be/')?.[1]?.split('?')?.[0]
    }
  }

  return link
}

export function Overview({
  embed,
  onEmbedChange,
  stores,
  isActiveTab
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
  stores: Record<string, Store>
  isActiveTab?: boolean
}) {
  const [viewport] = useStore((store) => ({
    md: store.viewport.md,
    xl: store.viewport.xl
  }))

  const onAttrChange = <K extends keyof LevelingEmbed>(newValue: LevelingEmbed[K], attr: K) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      return { ...embed, [attr]: newValue }
    })
  const onOverviewAttrChange = <K extends keyof LevelingOverview>(newValue: LevelingOverview[K], attr: K) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      return {
        ...embed,
        overview: {
          ...embed.overview,
          [attr]: newValue
        }
      }
    })

  const overviewEquipment = useEmbed(stores, embed.overview.equipment, 'equipment')
  const overviewAscendancy = useEmbed(stores, embed.overview.ascendancy, 'ascendancy')
  const overviewPantheon = useEmbed(stores, embed.overview.pantheon, 'pantheon')

  const [isIntroEditing, setIsIntroEditing] = React.useState(false)

  return (
    <div className="poe-overview-tab">
      <div className="poe-col-1">
        {!viewport.md ? (
          <Widget className="poe-author" title="Author">
            <ContentCreator embed={embed} onEmbedChange={onEmbedChange} />
          </Widget>
        ) : undefined}

        <Widget className="poe-introduction" title="Description">
          <div className="top">
            <LevelingTitleBox embed={embed} onEmbedChange={onEmbedChange} stores={stores} />

            <div className="poe-intro-separator"></div>

            <DifficultyBar embed={embed} onEmbedChange={onEmbedChange} />

            {!!onEmbedChange && (
              <span>
                {isIntroEditing ? (
                  <button onClick={() => setIsIntroEditing(false)}>Close</button>
                ) : (
                  <FontAwesomeIcon
                    icon={faEdit}
                    onClick={() => {
                      setIsIntroEditing(true)
                    }}
                  />
                )}
              </span>
            )}
            <RichTextEditor
              render={true}
              key={`${embed.id}-overview-introduction-content`}
              value={embed?.overview?.introduction}
              isEditing={isIntroEditing}
              onChange={
                onEmbedChange
                  ? (newValue) => {
                      onOverviewAttrChange(newValue, 'introduction')
                    }
                  : undefined
              }
            />

            <div className="poe-strengths-and-weaknesses-wrapper">
              <StrengthsAndWeaknesses embed={embed} onEmbedChange={onEmbedChange} />
            </div>
          </div>
        </Widget>

        {viewport.md && !viewport.xl ? <Strategies embed={embed} onEmbedChange={onEmbedChange} /> : undefined}
      </div>

      <div className="poe-col-2">
        {viewport.md && !viewport.xl ? (
          <Widget className="poe-author" title="Author">
            <ContentCreator embed={embed} onEmbedChange={onEmbedChange} />
          </Widget>
        ) : undefined}
        {(onEmbedChange || embed?.overview?.showcaseVideo) && (
          <Widget
            title={
              <>
                Build Showcase
                {onEmbedChange && (
                  <EditableText
                    value={embed.overview.showcaseVideo ?? ''}
                    placeholder="Video Link"
                    buttonPlaceholder="Edit Video Link"
                    onChange={(newValue) => onOverviewAttrChange(getYoutubeVideoId(newValue), 'showcaseVideo')}
                  />
                )}
              </>
            }>
            <div className="poe-video-box">
              <div className="poe-video">
                <iframe
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen={true}
                  src={`https://www.youtube.com/embed/${embed?.overview?.showcaseVideo}`}></iframe>
              </div>
            </div>
          </Widget>
        )}
        <Widget title="Equipment" className="poe-equip-group">
          <div className="poe-equip-paperdoll">
            {overviewEquipment && (
              <WithStore store={overviewEquipment.store}>
                <Paperdoll items={overviewEquipment.embed.items} />
              </WithStore>
            )}
          </div>
        </Widget>

        <PlannerLink embed={embed} onEmbedChange={onEmbedChange} />

        <PobLinks embed={embed} onEmbedChange={onEmbedChange} />

        <Widget title="Ascendancy" className="poe-ascendancy-group">
          <div className="poe-ascendancy-container">
            {isActiveTab && overviewAscendancy && overviewAscendancy.embed && (
              <WithStore store={overviewAscendancy.store}>
                <AscendancyEmbedView embed={overviewAscendancy.embed} active={overviewAscendancy.active} />
              </WithStore>
            )}
          </div>
        </Widget>

        <Widget title="Pantheon & Bandits" className="poe-panth-and-bandits">
          <div className="poe-pantheon" title="Pantheon">
            {/* <span className="poe-title">Pantheon</span> */}
            <span className="poe-pantheon-nodes">
              {overviewPantheon?.embed?.active && (
                <Store.Provider store={overviewPantheon.store || stores['default']}>
                  {Object.keys(overviewPantheon.embed.active).map((item, index) => {
                    const panth = Data.pantheon[item]
                    return (
                      <span key={index}>
                        <RawTooltip tooltip={<PantheonTooltip id={item} flags={[true, true, true]} />}>
                          <span>{panth.effects[0].name}</span>
                        </RawTooltip>
                      </span>
                    )
                  })}
                </Store.Provider>
              )}
            </span>
          </div>

          <div className="poe-bandits" title="Bandits">
            {/* <span className="poe-title">Bandits</span> */}
            <span>{banditOptions.find((item) => item.id === embed.overview?.bandits)?.name || ''}</span>
          </div>
        </Widget>

        {viewport.md && !viewport.xl ? <CraftingGuides embed={embed} onEmbedChange={onEmbedChange} /> : undefined}
        {viewport.md && !viewport.xl ? <BuildsList title={'Other Builds'} count={2} /> : undefined}
      </div>

      <div className="poe-col-3">
        <div className="poe-col-inner">
          {viewport.xl ? (
            <Widget className="poe-author" title="Author">
              <ContentCreator embed={embed} onEmbedChange={onEmbedChange} />
            </Widget>
          ) : undefined}

          {(!viewport.md || viewport.xl) && <CraftingGuides embed={embed} onEmbedChange={onEmbedChange} />}

          {!viewport.md || viewport.xl ? <Strategies embed={embed} onEmbedChange={onEmbedChange} /> : undefined}

          {!viewport.md || viewport.xl ? <BuildsList title={'Other Builds'} count={2} /> : undefined}
        </div>
      </div>
    </div>
  )
}
